/**
 * Citation parsing for assistant chat messages.
 *
 * The edge function returns prose containing inline citations of the form
 * `[ref:UUID]`. This module splits the prose into renderable segments so the UI
 * can replace each citation with a tap-link that opens the event detail modal.
 *
 * The regex is locked (ARCHITECTURE §5 / CLAUDE §8) — do not edit without
 * updating the edge function and the architecture doc in lockstep.
 */

export const CITATION_REGEX =
  /\[ref:([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\]/g;

export const CANONICAL_REFUSAL = "I don't have that in the records.";

export const CANONICAL_DISCLAIMER =
  'This app stores records. It does not provide medical advice. For clinical decisions, consult a licensed clinician.';

export type ChatSegment =
  | { kind: 'text'; text: string }
  | { kind: 'citation'; eventId: string; index: number };

/**
 * Split an assistant message into text and citation segments.
 *
 * Order of segments is preserved so the renderer can stream them inline.
 * Citations are numbered 1..N in order of first appearance so the UI can show
 * "[1]", "[2]", … chip labels consistently when the same event is cited twice.
 */
export function parseCitations(content: string): ChatSegment[] {
  const segments: ChatSegment[] = [];
  const order: string[] = [];

  let cursor = 0;
  for (const match of content.matchAll(CITATION_REGEX)) {
    const uuid = match[1];
    const start = match.index ?? 0;

    if (start > cursor) {
      segments.push({ kind: 'text', text: content.slice(cursor, start) });
    }

    let index = order.indexOf(uuid);
    if (index === -1) {
      order.push(uuid);
      index = order.length - 1;
    }
    segments.push({ kind: 'citation', eventId: uuid, index: index + 1 });
    cursor = start + match[0].length;
  }

  if (cursor < content.length) {
    segments.push({ kind: 'text', text: content.slice(cursor) });
  }

  return segments;
}

export function isCanonicalRefusal(content: string): boolean {
  return content.trim().toLowerCase() === CANONICAL_REFUSAL.toLowerCase();
}
